import { Alert, Box } from '@mui/material';
import { Megaphone, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';

import { ROLE_LABELS, createAdminApi, roleSatisfies, type AdminApi, type AdminMaintenance, type AdminRole } from '@/admin/adminApi';
import { isForbidden, messageOf } from '@/admin/users/adminErrors';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { useHttpClient } from '@/app/providers';
import { AccessDenied, Button, ErrorState, Icon, LoadingState, PageHeader } from '@/ui/kit';

import { MobileLimitNotice } from './maintenanceCopy';
import { lastWriteOf, readMaintenance, type LastWrite } from './maintenanceModel';
import { MaintenancePanel } from './MaintenancePanel';

/**
 * Reglages globaux (`/settings`, maquette P15) : pour l'instant, la seule
 * rubrique « Maintenance ».
 *
 * Les autres rubriques de la maquette — annonces, securite, courriels, alertes
 * d'exploitation — **ne sont pas affichees** : aucun reglage serveur ne les
 * porte (ADR 0024), et une rubrique sans reglage est une promesse sans objet.
 * « Fonctionnalites » les rejoindra avec la route publique de configuration.
 *
 * Trois regles :
 * 1. **Le role vient du serveur** (`/admin/me`), jamais deduit. Un compte sans
 *    role, ou un 403, est une explication et non une panne.
 * 2. **Pas de rafraichissement automatique** (ADR 0035 §4) : « Actualiser » et la
 *    relecture au retour sur l'onglet. La relecture garde le brouillon en cours.
 * 3. **Rien n'est invente** : l'etat du service est celui que le serveur calcule.
 */

interface Loaded {
  readonly role: AdminRole;
  readonly email: string;
  readonly maintenance: AdminMaintenance;
  readonly lastWrite: LastWrite | null;
}

/** Lecture complete de l'ecran. `null` : le compte n'a aucun role d'administration. */
const readScreen = async (api: AdminApi): Promise<Loaded | null> => {
  // Le role vient du serveur et de lui seul (ADR 0032).
  const identity = await api.identity();

  if (identity.adminRole === null) {
    return null;
  }

  const [body, settings] = await Promise.all([api.maintenance(), api.listSettings()]);
  const maintenance = readMaintenance(body);

  if (maintenance === null) {
    throw new Error('Le serveur a répondu autre chose qu’un état de maintenance.');
  }

  return {
    role: identity.adminRole,
    email: identity.email,
    maintenance,
    lastWrite: lastWriteOf(settings.items),
  };
};

interface Outcome {
  /** Tentative qui a produit cette issue : l'attente en est deduite. */
  readonly attempt: number;
  /** Derniere lecture connue, gardee apres un echec de relecture. */
  readonly loaded: Loaded | null;
  readonly noRole: boolean;
  readonly error: string | null;
  readonly denied: boolean;
}

const useMaintenanceScreen = (api: AdminApi) => {
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const loaded = await readScreen(api);

        if (!cancelled) {
          setOutcome({ attempt, loaded, noRole: loaded === null, error: null, denied: false });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setOutcome((previous) => ({
            attempt,
            loaded: previous?.loaded ?? null,
            noRole: false,
            // Un 403 a la LECTURE est un compte sans role : `AdminAccessGuard`
            // rend 403 a un compte parent ordinaire.
            error: isForbidden(error) ? null : messageOf(error),
            denied: isForbidden(error),
          }));
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [api, attempt]);

  const reload = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(reload);

  /** Relit apres une ecriture reussie. Rend `false` si la relecture echoue. */
  const reread = useCallback(async (): Promise<boolean> => {
    try {
      const loaded = await readScreen(api);

      setOutcome(() => ({
        attempt,
        loaded,
        noRole: loaded === null,
        error: null,
        denied: false,
      }));

      return loaded !== null;
    } catch {
      return false;
    }
  }, [api, attempt]);

  const settled = outcome?.attempt === attempt ? outcome : null;

  return {
    busy: settled === null,
    loaded: outcome?.loaded ?? null,
    noRole: outcome?.noRole ?? false,
    error: settled?.error ?? null,
    denied: settled?.denied ?? false,
    reload,
    reread,
  };
};

const SubNavigation = (): ReactElement => (
  <Box component="nav" aria-label="Rubriques des réglages" sx={{ alignSelf: 'start' }}>
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box component="li">
        <Box
          component="span"
          aria-current="page"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1.125,
            borderRadius: '10px',
            bgcolor: 'tokens.primarySoft',
            color: 'tokens.primary',
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          <Icon icon={Megaphone} size={18} />
          Maintenance
        </Box>
      </Box>
    </Box>
  </Box>
);

const RoleNotice = ({ role }: { readonly role: AdminRole }): ReactElement | null =>
  roleSatisfies(role, 'admin') ? null : (
    <Alert severity="info" variant="outlined">
      <strong>Lecture seule.</strong> Votre rôle est « {ROLE_LABELS.support} » : vous voyez la maintenance, vous ne
      pouvez pas la modifier. Les commandes d’écriture ne sont donc pas proposées — c’est le serveur qui tranche, et il
      refuserait l’écriture.
    </Alert>
  );

export const GlobalSettingsScreen = (): ReactElement => {
  const http = useHttpClient();
  const api = useMemo(() => createAdminApi(http), [http]);
  const { busy, loaded, noRole, error, denied, reload, reread } = useMaintenanceScreen(api);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        eyebrow="Réglages"
        title="Réglages globaux"
        subtitle="Ils valent pour tous les comptes et pour l’application mobile. Chaque changement demande un motif et laisse une ligne au journal."
        actions={
          <Button icon={RefreshCw} onClick={reload} disabled={busy}>
            {busy ? 'Actualisation…' : 'Actualiser'}
          </Button>
        }
      />

      {denied || noRole ? (
        <AccessDenied title="Votre compte n’a pas accès à cet écran">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration. Un rôle se donne sur le serveur, par la
          commande <code>php bin/console app:admin:grant</code>, jamais depuis un écran (ADR 0032).
        </AccessDenied>
      ) : null}

      {!denied && !noRole && loaded === null && busy ? <LoadingState label="Lecture de l’état de la maintenance…" /> : null}

      {error !== null ? (
        <ErrorState title="La maintenance n’a pas pu être lue" message={error} onRetry={reload} />
      ) : null}

      {loaded === null || denied || noRole ? null : (
        <>
          <RoleNotice role={loaded.role} />

          <Alert severity="warning" variant="outlined">
            <MobileLimitNotice />
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px minmax(0, 1fr)' }, gap: 2 }}>
            <SubNavigation />
            <Box sx={{ minWidth: 0 }}>
              <MaintenancePanel
                api={api}
                state={loaded.maintenance}
                lastWrite={loaded.lastWrite}
                canWrite={roleSatisfies(loaded.role, 'admin')}
                operatorEmail={loaded.email}
                onSaved={reread}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
